import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { type Session, type User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AccessStatus = {
  access_expires_at: string | null;
  blocked_reason?: string | null;
  cancel_effective_at?: string | null;
  cancel_protocol_code?: string | null;
  cancel_scheduled_at?: string | null;
  cancellation_type?: "REGRET_7_DAYS" | "CANCEL_SCHEDULED" | null;
  customer_access_status?:
    | "pending_payment"
    | "active"
    | "grace_period"
    | "blocked_nonpayment"
    | "expired"
    | "cancelled"
    | "suspended"
    | "purged"
    | "cancel_scheduled"
    | "refund_pending"
    | null;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  is_authorized: boolean;
  reason:
    | "active"
    | "cancel_scheduled"
    | "grace_period"
    | "suspended"
    | "purged"
    | "expired"
    | "inactive"
    | "not_listed"
    | "regret_window";
  role: "admin" | "customer";
  plan_code: string | null;
  subscription_status: "manual" | "trial" | "active" | "grace_period" | "expired" | "canceled";
  trial_started_at: string | null;
  trial_ends_at: string | null;
  subscription_started_at: string | null;
  subscription_ends_at: string | null;
  current_price_cents: number | null;
  grace_period_until?: string | null;
  suspended_at?: string | null;
  purged_at?: string | null;
  payment_method?: string | null;
  refund_expected_at?: string | null;
  refund_requested_at?: string | null;
  regret_window_until?: string | null;
  renewal_url?: string | null;
  scheduled_plan_code?: string | null;
  scheduled_change_at?: string | null;
  scheduled_change_reason?: string | null;
};

type UserProfile = {
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  birth_date: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  accepted_at: string | null;
  auto_logout_minutes: number | null;
};

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  access: AccessStatus | null;
  authError: string | null;
  isAdmin: boolean;
  isAuthorized: boolean;
  loading: boolean;
  /** True while a PKCE code exchange is in flight. Consumers (e.g. auto-logout)
   *  must not call signOut() when this is true or the code_verifier will be
   *  destroyed before exchangeCodeForSession completes. */
  isExchangingCode: boolean;
  signInWithGoogle: (redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ACCESS_DENIED_MESSAGE =
  "Sua conta Google foi autenticada, mas o acesso ao sistema não está liberado. Fale com o administrador.";
const LOGGING_OUT_KEY = "dailyfi-logging-out";
/** Stores the ?next= path before OAuth so it survives even if Supabase
 *  strips query params from the callback URL. */
const OAUTH_NEXT_KEY = "dailyfi-oauth-next";
const RENEWAL_REASONS = new Set<AccessStatus["reason"]>(["grace_period", "suspended", "purged", "expired"]);
// Guard against duplicate geo lookups during the OAuth/bootstrap flow.
let hasAttemptedAccessHistoryGeoLookup = false;

function shouldRouteToRenewal(access: AccessStatus | null) {
  return Boolean(access && !access.is_authorized && RENEWAL_REASONS.has(access.reason));
}

function getAccessErrorMessage(access: AccessStatus | null) {
  if (!access) return ACCESS_DENIED_MESSAGE;

  if (access.reason === "regret_window" && access.blocked_reason === "regret_window") {
    return "Seu cancelamento com estorno está em processamento.";
  }

  if (access.reason === "grace_period") {
    return "Seu acesso entrou em período de carência. Regularize a assinatura para evitar a suspensão do DailyFi.";
  }

  if (access.reason === "suspended" && access.blocked_reason === "user_requested_cancellation") {
    return "Sua assinatura foi encerrada a pedido ao final do ciclo. Você ainda pode reativar o DailyFi durante a janela de retenção dos seus dados.";
  }

  if (access.reason === "purged" && access.blocked_reason === "data_purged") {
    return "A janela de reativação terminou e os dados anteriores já foram removidos conforme a política de retenção.";
  }

  if (access.blocked_reason === "user_requested_cancellation") {
    return "Sua assinatura foi encerrada a pedido e o acesso foi bloqueado ao final do ciclo vigente.";
  }

  if (access.reason === "suspended" || access.reason === "purged") {
    return "Seu acesso está suspenso por falta de pagamento. Regularize sua assinatura para reativar o DailyFi.";
  }

  if (access.reason === "expired") {
    if (access.plan_code) {
      return "Seu acesso está bloqueado por falta de pagamento ou vencimento da assinatura. Regularize sua assinatura para continuar usando o DailyFi.";
    }
    return "Seu acesso expirou. Fale com o administrador para renovar sua licença.";
  }

  if (access.reason === "inactive") {
    return "Seu acesso está inativo no momento. Fale com o administrador.";
  }

  return ACCESS_DENIED_MESSAGE;
}

async function fetchProfile(userId: string) {
  const { data } = await supabase
    .from("profiles")
    .select("full_name, avatar_url, phone, birth_date, country, state, city, accepted_at, auto_logout_minutes")
    .eq("id", userId)
    .single();

  return (data ?? null) as UserProfile | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [access, setAccess] = useState<AccessStatus | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isExchangingCode, setIsExchangingCode] = useState(false);
  // Tracks whether a session is active — used by the visibilitychange fallback
  const hasSessionRef = useRef(false);
  // Prevents syncProfile from calling signOut() while a PKCE code exchange is in
  // progress. Without this guard, a TOKEN_REFRESHED or stale-session event fires
  // onAuthStateChange → syncProfile → signOut() → clears localStorage including
  // the code_verifier → exchangeCodeForSession fails → "first login always fails".
  const codeExchangeActiveRef = useRef(false);

  const refreshProfile = async () => {
    if (!user) {
      setProfile(null);
      return;
    }
    const nextProfile = await fetchProfile(user.id);
    setProfile(nextProfile);
  };

  useEffect(() => {
    const clearAuthParamsFromUrl = () => {
      const url = new URL(window.location.href);
      const paramsToClear = ["code", "error", "error_code", "error_description", "state"];
      let changed = false;

      for (const param of paramsToClear) {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          changed = true;
        }
      }

      if (changed) {
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState({}, document.title, nextUrl);
      }
    };

    const syncProfile = async (currentSession: Session | null, recordAccess = false) => {
      hasSessionRef.current = Boolean(currentSession?.user);

      if (!currentSession?.user) {
        setSession(null);
        setUser(null);
        setProfile(null);
        setAccess(null);
        setLoading(false);
        return;
      }

      const { data: accessData, error: accessError } = await supabase.rpc("get_my_access_status");

      if (accessError) {
        // If a PKCE code exchange is active, this RPC failure is from a stale/concurrent
        // session event — do not sign out (that would destroy the code_verifier).
        // The exchange itself will call syncProfile again with the fresh session.
        if (codeExchangeActiveRef.current) {
          return;
        }
        setProfile(null);
        setAccess(null);
        setAuthError("Não foi possível validar sua licença de acesso. Tente novamente.");
        await supabase.auth.signOut();
        hasSessionRef.current = false;
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      const nextAccess = accessData as AccessStatus | null;
      const nextProfile = await fetchProfile(currentSession.user.id);

      if (!nextAccess?.is_authorized) {
        if (!shouldRouteToRenewal(nextAccess)) {
          // Fazer logout silenciosamente sem mostrar erro de "não autorizado".
          // Guard: if a PKCE exchange is active, skip signOut to preserve code_verifier.
          if (codeExchangeActiveRef.current) {
            return;
          }
          await supabase.auth.signOut();
          hasSessionRef.current = false;
          setSession(null);
          setUser(null);
          setProfile(null);
          setAccess(null);
          setAuthError(null);
          setLoading(false);
          return;
        }

        // Se está em período de renovação — set ALL state atomically, then show error
        setSession(currentSession);
        setUser(currentSession.user);
        setProfile(nextProfile);
        setAccess(nextAccess);
        setAuthError(getAccessErrorMessage(nextAccess));
        setLoading(false);
        return;
      }

      // Authorized — set ALL state atomically so components never see a partial state
      // (e.g. user set but access still null → "acesso não liberado" flash)
      setSession(currentSession);
      setUser(currentSession.user);
      setProfile(nextProfile);
      setAccess(nextAccess);
      setAuthError(null);
      setLoading(false);

      // Record access history only on actual login (SIGNED_IN event).
      // Geolocalização é resolvida server-side pela Edge Function record-access-history,
      // eliminando a chamada ao ipapi.co no cliente e garantindo que o IP real
      // (após proxies/CDN) seja capturado com precisão pelo servidor.
      if (recordAccess && !hasAttemptedAccessHistoryGeoLookup) {
        hasAttemptedAccessHistoryGeoLookup = true;
        const ua = navigator.userAgent;
        const deviceType = /mobile/i.test(ua) ? "mobile" : /tablet/i.test(ua) ? "tablet" : "desktop";
        const accessToken = currentSession.access_token;
        // Fire-and-forget — nunca bloqueia o fluxo de login
        (async () => {
          try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
            const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
            await fetch(`${supabaseUrl}/functions/v1/record-access-history`, {
              method: "POST",
              headers: {
                // O gateway do Supabase exige `apikey` em todas as requisições a
                // Edge Functions, independente do verify_jwt. Sem ele, o gateway
                // rejeita com 401 antes mesmo de o código Deno executar.
                "apikey": anonKey,
                "Authorization": `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ user_agent: ua, device_type: deviceType }),
            });
          } catch { /* histórico de acesso não é crítico para o login */ }
        })();
      }
    };

    // ─── CRITICAL: Exchange OAuth code BEFORE registering onAuthStateChange ───
    // When the user returns from Google with ?code=xxx, we MUST exchange the
    // PKCE code before anything else.  If we register onAuthStateChange first,
    // Supabase fires INITIAL_SESSION with the stale/expired session from
    // localStorage → syncProfile calls get_my_access_status → fails → signOut()
    // clears localStorage INCLUDING the code_verifier → exchangeCodeForSession
    // fails because the verifier is gone.  This is why login always required
    // two attempts.
    //
    // Solution: detect ?code early, exchange it, THEN register the listener.
    // ────────────────────────────────────────────────────────────────────────

    const url = new URL(window.location.href);
    const pendingCode = url.searchParams.get("code");
    const callbackError = url.searchParams.get("error_description") ?? url.searchParams.get("error");

    // Clear OAuth params from the URL bar immediately (cosmetic only)
    if (pendingCode || callbackError) {
      clearAuthParamsFromUrl();
    }

    // Handle OAuth errors first
    if (callbackError) {
      setAuthError(decodeURIComponent(callbackError.replace(/\+/g, " ")));
      setLoading(false);
      // Still register listener for future auth events
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
        void syncProfile(nextSession, event === "SIGNED_IN");
      });
      return () => { subscription.unsubscribe(); };
    }

    // If we have an OAuth code, exchange it BEFORE registering onAuthStateChange
    // so the stale-session INITIAL_SESSION event can't race and clear code_verifier.
    let codeExchangePromise: Promise<void> | null = null;

    if (pendingCode) {
      codeExchangeActiveRef.current = true;
      setIsExchangingCode(true);
      codeExchangePromise = (async () => {
        try {
          // Tenta trocar o code até 3 vezes para acomodar:
          // (a) novo deploy do Service Worker que invalida o localStorage entre
          //     o redirect do Google e a chegada do code_verifier na nova versão;
          // (b) latência de rede elevada no primeiro request pós-redirect.
          let exchangeData = null;
          let exchangeError = null;

          for (let attempt = 0; attempt < 3; attempt++) {
            const result = await supabase.auth.exchangeCodeForSession(pendingCode);
            if (!result.error) {
              exchangeData = result.data;
              exchangeError = null;
              break;
            }
            exchangeError = result.error;
            if (attempt < 2) {
              // Backoff progressivo: 1.2s → 2.4s
              await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
            }
          }

          if (exchangeError) {
            setAuthError("Não foi possível validar o retorno do Google. Tente novamente.");
            setLoading(false);
            return;
          }
          if (exchangeData?.session) {
            await syncProfile(exchangeData.session, true);
          }
        } finally {
          // Always clear the guard so future events are processed normally
          codeExchangeActiveRef.current = false;
          setIsExchangingCode(false);
        }
      })();
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // While a PKCE code exchange is active, suppress ALL auth events except
      // SIGNED_OUT. Any session event here (INITIAL_SESSION, TOKEN_REFRESHED,
      // SIGNED_IN from a stale session) can trigger syncProfile → signOut() →
      // clear code_verifier in localStorage → exchange fails.
      // The exchange itself calls syncProfile directly when it completes.
      if (codeExchangeActiveRef.current && event !== "SIGNED_OUT") return;
      void syncProfile(nextSession, event === "SIGNED_IN");
    });

    const initializeAuth = async () => {
      // If there was a pending code, wait for the exchange to finish
      if (codeExchangePromise) {
        await codeExchangePromise;
        return;
      }

      // Normal load — check for an existing persisted session
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      await syncProfile(currentSession);
    };

    void initializeAuth();

    // Android/iOS PWA fallback: when a Chrome Custom Tab (or SFSafariViewController)
    // handles the OAuth callback and establishes a session in shared storage, the PWA
    // is backgrounded during that time. When it regains focus, we re-check for a new
    // session. A small delay allows shared storage (localStorage) to propagate the
    // session written by the CCT before we read it.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // If we already have a session, no need to poll — onAuthStateChange handles it.
      if (hasSessionRef.current) return;

      // Wait briefly for localStorage session to be written by the CCT/browser tab
      setTimeout(async () => {
        // Double-check in case onAuthStateChange fired during the delay
        if (hasSessionRef.current) return;
        const { data: { session: latestSession } } = await supabase.auth.getSession();
        if (latestSession) {
          await syncProfile(latestSession, true);
        }
      }, 300);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const signInWithGoogle = async (redirectTo?: string) => {
    setAuthError(null);
    window.sessionStorage.removeItem(LOGGING_OUT_KEY);

    // Build callback URL ensuring the /app prefix is always present.
    // Supabase may strip the path and fall back to site_url if the
    // redirectTo doesn't match the dashboard allow-list exactly.
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("code");
    currentUrl.searchParams.delete("error");
    currentUrl.searchParams.delete("error_code");
    currentUrl.searchParams.delete("error_description");

    // Guarantee /app prefix — if somehow the page loaded without it,
    // force the callback to go through /app so the React app handles
    // the code exchange.
    if (!currentUrl.pathname.startsWith("/app")) {
      currentUrl.pathname = "/app" + currentUrl.pathname;
    }
    const callbackUrl = currentUrl.toString();

    // Persist the ?next= path so it survives even when Supabase strips
    // query params and redirects to site_url without them.
    const nextPath = currentUrl.searchParams.get("next");
    if (nextPath) {
      window.sessionStorage.setItem(OAUTH_NEXT_KEY, nextPath);
    } else {
      window.sessionStorage.removeItem(OAUTH_NEXT_KEY);
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectTo ?? callbackUrl,
        skipBrowserRedirect: true,
      },
    });

    if (error) throw error;
    if (!data?.url) return;

    // Always redirect the current tab — avoids any cross-tab/cross-window issues on mobile PWAs
    window.location.href = data.url;
  };

  const signOut = async () => {
    // Set flag so ProtectedRoute knows this is an intentional logout (not session loss)
    window.sessionStorage.setItem(LOGGING_OUT_KEY, "1");
    hasSessionRef.current = false;
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setProfile(null);
    setAccess(null);
    setAuthError(null);
    // Navigate with full page reload — the flag is consumed and cleared by
    // ProtectedRoute on the next page load so it doesn't persist across logins.
    window.location.replace('/app?login=1');
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        access,
        authError,
        isAdmin: access?.role === "admin",
        isAuthorized: access?.is_authorized ?? false,
        loading,
        isExchangingCode,
        signInWithGoogle,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
